import * as preact from "preact";

import * as deviceDetect from "deviceDetect";
import * as sourceUtils from "shared/sourceUtils";
import { cn } from "shared/utils";

interface SourceItem {
  source: string;
  title?: string;
  author?: string;
  count?: number;
}

interface AllWordsOption {
  count: number;
  onSelect: () => void;
}

interface SingleSelectProps {
  multiSelect?: false;
  onSelect: (source: string) => void;
  selectedSource?: string;
}

interface MultiSelectProps {
  multiSelect: true;
  selectedSources: string[];
  onConfirm: (sources: string[]) => void;
  confirmLabel?: string;
}

type CommonProps = {
  sources: SourceItem[];
  allWords?: AllWordsOption;
  /**
   * When set, sources are grouped under a "Recently read" heading and only
   * the first `recentlyReadLimit` are shown until the user clicks "Show
   * more". Filtering ignores the limit so users can find any matching
   * source. Without this prop, all sources render immediately.
   */
  recentlyReadLimit?: number;
  /** Start with the source list already expanded (e.g. when the persisted
   * source lives past the limit). */
  initiallyExpanded?: boolean;
};

type Props = CommonProps & (SingleSelectProps | MultiSelectProps);

interface State {
  filter: string;
  selectedIndex: number;
  expanded: boolean;
  pendingSources: string[];
}

const ALL_WORDS_SENTINEL = "__all__";

/** Strip diacritics so "leon" matches "León" and "metamorfosis" matches "Metamórfosis". */
const foldForSearch = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

export default class SourcePickerModal extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      filter: "",
      selectedIndex: 0,
      expanded: !!props.initiallyExpanded,
      pendingSources: props.multiSelect ? [...props.selectedSources] : [],
    };
  }

  toggleSource(source: string) {
    const { pendingSources } = this.state;
    if (pendingSources.includes(source)) {
      this.setState({
        pendingSources: pendingSources.filter((item) => item !== source),
      });
    } else {
      this.setState({ pendingSources: [...pendingSources, source] });
    }
  }

  render() {
    const { sources, allWords, recentlyReadLimit } = this.props;
    const multiSelect = this.props.multiSelect === true;
    const { filter, selectedIndex, expanded, pendingSources } = this.state;
    const selectedSource = !multiSelect
      ? (this.props as SingleSelectProps).selectedSource
      : undefined;

    const filterFolded = foldForSearch(filter.trim());
    const isFiltering = filterFolded.length > 0;

    const filteredSources = sources.filter((item) => {
      if (!filterFolded) return true;
      const displayName = foldForSearch(
        sourceUtils.formatSourceDisplayName(
          item.source,
          item.title,
          item.author
        )
      );
      const aliases = sourceUtils
        .getSourceFilterAliases(item.source)
        .map(foldForSearch);
      return (
        displayName.includes(filterFolded) ||
        aliases.some((alias) => alias.includes(filterFolded))
      );
    });

    // Shelves stand apart from the recently-read texts: there are only ever a
    // handful, and burying them behind "Show more" would hide the feature.
    const shelfSources = filteredSources.filter((item) =>
      sourceUtils.isShelfSource(item.source)
    );
    const textSources = filteredSources.filter(
      (item) => !sourceUtils.isShelfSource(item.source)
    );

    // Collapse the list only when not filtering. When filtering, users want
    // to see every match regardless of the recently-read window.
    const shouldCollapse =
      recentlyReadLimit !== undefined &&
      !expanded &&
      !isFiltering &&
      textSources.length > recentlyReadLimit;

    const visibleTexts = shouldCollapse
      ? textSources.slice(0, recentlyReadLimit)
      : textSources;
    const hiddenCount = textSources.length - visibleTexts.length;

    // Keyboard navigation runs down the rendered rows in order.
    const visibleSources = [...shelfSources, ...visibleTexts];

    const showAllWords =
      !!allWords && (!filterFolded || "all words".includes(filterFolded));
    const allWordsOffset = showAllWords ? 1 : 0;
    const showMoreOffset = shouldCollapse ? 1 : 0;
    // Selectable rows for keyboard nav: All Words + visible sources + Show more.
    const totalRows = visibleSources.length + allWordsOffset + showMoreOffset;

    const expandList = () => {
      this.setState({ expanded: true, selectedIndex: 0 });
    };

    const handleAllWords = () => {
      if (multiSelect) {
        this.setState({ pendingSources: [] });
        return;
      }
      allWords!.onSelect();
    };

    const handleSourcePick = (source: string) => {
      if (multiSelect) {
        this.toggleSource(source);
        return;
      }
      (this.props as SingleSelectProps).onSelect(source);
    };

    const activateRow = (rowIndex: number) => {
      if (showAllWords && rowIndex === 0) {
        handleAllWords();
        return;
      }
      const sourceIndex = rowIndex - allWordsOffset;
      if (sourceIndex < visibleSources.length) {
        handleSourcePick(visibleSources[sourceIndex].source);
        return;
      }
      if (shouldCollapse && sourceIndex === visibleSources.length) {
        expandList();
      }
    };

    // Whether to render the "Recently read" section heading. We render it
    // whenever the recently-read grouping is active and at least one source
    // would appear under it.
    const showRecentlyReadHeading =
      recentlyReadLimit !== undefined && visibleTexts.length > 0;

    const columnCount = multiSelect ? 3 : 2;

    const groupHeading = (label: string) => (
      <tr class="sourceGroupHeading">
        <td colSpan={columnCount}>{label}</td>
      </tr>
    );

    const sourceRow = (item: SourceItem, rowIndex: number) => {
      const displayName = sourceUtils.formatSourceDisplayName(
        item.source,
        item.title,
        item.author
      );
      const isChecked = pendingSources.includes(item.source);
      const selected = multiSelect
        ? isChecked
        : filter === ""
          ? selectedSource === item.source
          : rowIndex === selectedIndex;
      return (
        <tr
          key={item.source}
          class={cn({ selected })}
          onClick={(event) => {
            event.stopPropagation();
            handleSourcePick(item.source);
          }}
        >
          {multiSelect ? (
            <td class="sourceColCheck">
              <input type="checkbox" checked={isChecked} />
            </td>
          ) : null}
          <td class="sourceColSource">
            <span class="sourceEmoji">
              {sourceUtils.getSourceEmoji(item.source)}
            </span>
            {displayName}
          </td>
          <td class="sourceColCount">
            {item.count !== undefined ? item.count : ""}
          </td>
        </tr>
      );
    };

    const confirmLabel = multiSelect
      ? (this.props as MultiSelectProps).confirmLabel ?? "Done"
      : null;

    return (
      <div class="sourcePickerModal">
        <input
          ref={(input) => {
            if (input && !deviceDetect.isSmall()) {
              setTimeout(() => {
                input.focus();
              }, 100);
            }
          }}
          type="text"
          placeholder="Filter sources..."
          value={filter}
          onInput={(event) =>
            this.setState({
              filter: event.currentTarget.value,
              selectedIndex: 0,
            })
          }
          autoFocus={!deviceDetect.isSmall()}
          class="sourceFilterInput"
          onKeyDown={(event) => {
            if (event.key === "Enter" && totalRows > 0) {
              activateRow(selectedIndex);
            }
            if (event.key === "ArrowDown") {
              this.setState({
                selectedIndex: Math.min(selectedIndex + 1, totalRows - 1),
              });
              event.preventDefault();
            }
            if (event.key === "ArrowUp") {
              this.setState({
                selectedIndex: Math.max(selectedIndex - 1, 0),
              });
              event.preventDefault();
            }
          }}
        />
        <div class="sourceListWrap">
          <table class={cn({ sourceList: true, multiSelect })}>
            <thead>
              <tr>
                {multiSelect ? <th class="sourceColCheck"></th> : null}
                <th class="sourceColSource">Source</th>
                <th class="sourceColCount">Words Ready</th>
              </tr>
            </thead>
            <tbody>
              {showAllWords && allWords ? (
                <tr
                  key={ALL_WORDS_SENTINEL}
                  class={cn({
                    // When filtering, the persisted highlight gives way to
                    // the keyboard cursor so only one row reads as active.
                    selected: multiSelect
                      ? pendingSources.length === 0
                      : filter === ""
                        ? selectedSource === undefined
                        : selectedIndex === 0,
                  })}
                  // No preventDefault on these row clicks: canceling the
                  // checkbox/radio's native toggle reverts dom.checked AFTER
                  // Preact's re-render, leaving the visible state a click
                  // behind. See RemoteWordListView.clickCheckbox (task 00314).
                  onClick={(event) => {
                    event.stopPropagation();
                    handleAllWords();
                  }}
                >
                  {multiSelect ? (
                    <td class="sourceColCheck">
                      <input
                        type="radio"
                        checked={pendingSources.length === 0}
                      />
                    </td>
                  ) : null}
                  <td class="sourceColSource">All Words</td>
                  <td class="sourceColCount">{allWords.count}</td>
                </tr>
              ) : null}
              {shelfSources.length > 0 ? groupHeading("Shelves") : null}
              {shelfSources.map((item, index) =>
                sourceRow(item, index + allWordsOffset)
              )}
              {showRecentlyReadHeading ? groupHeading("Recently read") : null}
              {visibleTexts.map((item, index) =>
                sourceRow(item, index + shelfSources.length + allWordsOffset)
              )}
              {shouldCollapse ? (
                <tr
                  key="__show_more__"
                  class={
                    "sourceShowMore " +
                    cn({
                      selected:
                        isFiltering &&
                        selectedIndex ===
                          visibleSources.length + allWordsOffset,
                    })
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    expandList();
                  }}
                >
                  <td colSpan={columnCount}>
                    Show more ({hiddenCount} more)...
                  </td>
                </tr>
              ) : null}
              {totalRows === 0 ? (
                <tr class="noResults">
                  <td colSpan={columnCount}>No matching sources</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {multiSelect ? (
          <div class="sourcePickerFooter">
            <button
              type="button"
              class="prominent sourcePickerConfirm"
              onClick={() => {
                (this.props as MultiSelectProps).onConfirm(pendingSources);
              }}
            >
              {confirmLabel}
            </button>
          </div>
        ) : null}
      </div>
    );
  }
}
